/* eslint-disable max-len */
import {
  Component,
  Host,
  h,
  Prop,
  Event,
  EventEmitter,
  Element,
  Watch,
} from '@stencil/core';
import { versionComponent } from '../../global/inject-dependencies';

@Component({
  tag: 'ui-quickfact',
  styleUrl: 'ui-quickfact.scss',
  shadow: true,
})
export class UiQuickfact {
  @Element() el: HTMLUiQuickfactElement;

  @Prop({ mutable: true }) iconSize: 'small' | 'large' = 'large';

  @Prop({ mutable: true }) iconLayout: 'above' | 'left' = 'above';

  @Prop() headline: string;

  @Prop() text: string;

  /**
   * If true, this means the text is being shown. The default is false, so the text is hidden.
   */
  @Prop({ reflect: true, mutable: true }) open = false;

  /**
   * Event responsible for the opening of the quickfact.
   */
  @Event({ eventName: 'quickfactClicked' }) quickfactClicked: EventEmitter;

  @Event({ eventName: 'quickfactExpanded' }) quickfactExpanded: EventEmitter;

  /**
   * New DOM element to place in the grid for the expandable content
   */
  expandableContent = document.createElement('div');

  /**
   * If true, the parent element of the quickfact component is the associated cms component
   */
  isParentCmsQuickfact;

  componentDidLoad() {
    // TODO refactor: remove cms logic from core component
    this.isParentCmsQuickfact =
      this.el.parentElement.nodeName === 'UI-CMS-QUICKFACT';
    this.onOpenChange();
  }

  hasContent(): boolean {
    if (this.el.children.length > 1) {
      return true;
    }
    if (!this.hasIcon()) {
      return true;
    }
    return false;
  }

  hasIcon(): boolean {
    return [...this.el.children].some(
      (child: HTMLElement) => child.getAttribute('slot') === 'icon'
    );
  }

  @Watch('open')
  onOpenChange() {
    const getExpandableContentElems = Array.from(this.el.children).filter(
      (elem) => !elem.hasAttribute('slot')
    );

    if (this.open && getExpandableContentElems.length) {
      this.expandableContent.innerHTML = '';
      getExpandableContentElems.forEach((elem) => {
        this.expandableContent.appendChild(elem.cloneNode(true));
      });
      this.expandableContent.style.gridColumn = '1 / -1';
      this.expandableContent.style.marginTop = '-32px';
      if (this.isParentCmsQuickfact) {
        this.quickfactExpanded.emit(this.expandableContent);
      } else {
        this.el.after(this.expandableContent);
      }
    } else {
      this.expandableContent.remove();
    }
  }

  onClick() {
    this.open = !this.open;
    this.quickfactClicked.emit({ quickfact: this.el });
  }

  render() {
    return (
      <Host>
        <div
          class={{
            container: true,
            'container--has-content': this.hasContent(),
            'container--has-icon': this.hasIcon(),
          }}
          part="container"
          onClick={this.onClick.bind(this)}
        >
          <div
            class={{
              content: true,
              'content--small': this.iconSize === 'small',
              'content--left': this.iconLayout === 'left',
            }}
          >
            <slot name="icon"></slot>
            <div class="text">
              <ui-richtext
                class="headline"
                innerHTML={this.headline}
              ></ui-richtext>
              <ui-richtext class="subline" innerHTML={this.text}></ui-richtext>
            </div>
          </div>
          {this.hasContent() && (
            <ui-icon
              class={{
                active: this.open,
                arrow: true,
              }}
              icon="chevron-bottom"
              type="system"
            ></ui-icon>
          )}
        </div>
        <div class="content" part="content">
          <slot />
        </div>
      </Host>
    );
  }
}

versionComponent(UiQuickfact);
